import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AnalyticsService,
  Insight,
  InsightsSummary,
  InsightPriority,
} from '../../../../services/analytics.service';
import { LogService } from '../../../../../services/log.service';

@Component({
  selector: 'app-insights-card',
  imports: [CommonModule],
  templateUrl: './insights-card.component.html',
  styleUrls: ['./insights-card.component.css'],
})
export class InsightsCardComponent implements OnInit {
  private log = inject(LogService);
  private analyticsService = inject(AnalyticsService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  insights: Insight[] = [];
  summary: InsightsSummary | null = null;
  isLoading = true;
  error: string | null = null;
  isExpanded = false;

  // Nombre d'insights à afficher quand replié
  private collapsedLimit = 3;

  ngOnInit(): void {
    this.loadInsights();
  }

  loadInsights(): void {
    this.isLoading = true;
    this.error = null;

    this.analyticsService
      .getInsights('today')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.insights = data.insights;
          this.summary = data.summary;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.log.error('Erreur chargement insights:', err);
          this.error = 'Impossible de charger les recommandations';
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  get visibleInsights(): Insight[] {
    return this.isExpanded
      ? this.insights
      : this.insights.slice(0, this.collapsedLimit);
  }

  get hasMore(): boolean {
    return this.insights.length > this.collapsedLimit;
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  onActionClick(insight: Insight): void {
    if (insight.action?.route) {
      this.router.navigate([insight.action.route]);
    }
  }

  getPriorityColor(priority: InsightPriority): string {
    const colors: Record<InsightPriority, string> = {
      critical: 'bg-red-100 border-red-300 text-red-800',
      high: 'bg-orange-100 border-orange-300 text-orange-800',
      medium: 'bg-yellow-100 border-yellow-300 text-yellow-800',
      low: 'bg-blue-100 border-blue-300 text-blue-800',
    };
    return colors[priority] || colors.low;
  }

  getPriorityIconBg(priority: InsightPriority): string {
    const colors: Record<InsightPriority, string> = {
      critical: 'bg-red-200',
      high: 'bg-orange-200',
      medium: 'bg-yellow-200',
      low: 'bg-blue-200',
    };
    return colors[priority] || colors.low;
  }

  getPriorityBadge(priority: InsightPriority): string {
    const labels: Record<InsightPriority, string> = {
      critical: 'Critique',
      high: 'Important',
      medium: 'Modéré',
      low: 'Info',
    };
    return labels[priority] || '';
  }

  getSummaryText(): string {
    if (!this.summary) return '';

    const parts: string[] = [];
    if (this.summary.critical > 0)
      parts.push(`${this.summary.critical} critique(s)`);
    if (this.summary.high > 0) parts.push(`${this.summary.high} important(s)`);
    if (this.summary.medium > 0) parts.push(`${this.summary.medium} modéré(s)`);

    return parts.length > 0
      ? parts.join(', ')
      : `${this.summary.total} recommandation(s)`;
  }
}
